// src/components/RecordDrawerHeader.jsx
//
// Task #9099 — OFFICE-DRAWER-HEADER-V2. The one shared record-drawer header
// presentation (identity · icon tools · pager · close) consumed by the Office,
// Market and Project drawers, and available to the Lease drawer later.
//
// Presentation only. Owners keep data, covers, guards and URL ownership and
// pass in:
//   eyebrow, title (node) + titleText (full identity for the title attribute),
//   meta (node) + metaText, share { path, testId }, fullPage { onClick, label,
//   testId } (only when a distinct supported reader exists), pager (props for
//   PrevNextPager, rendered in its icon variant with a truthful "n of total"),
//   onClose (the owner's guarded close; render SlideSheet with hideCloseButton
//   so exactly one close control exists), strip (node) and children.
//
// Fixed mode (`height` set, e.g. Office 184 incl. a 68px strip): every row is
// single-line with ellipsis, so no content can change the measured envelope.
// Flexible mode (no height): the meta row may wrap (Market chips, Project).
import { useEffect, useRef, useState } from "react";
import { Share, ExternalLink, X } from "lucide-react";
import { T } from "../app/tokens.js";
import { PrevNextPager } from "./PrevNextPager.jsx";
import { absoluteRecordUrl, copyText } from "./CopyLinkButton.jsx";
import { showToast } from "./toastService.jsx";

export const LINK_COPIED_TOAST = "Link copied";
const ROW1 = 32;

const one = { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

export const RECORD_HEADER_CSS = `
  .rdh-tool { position: relative; display: inline-grid; place-items: center; width: 30px; height: 30px; flex: 0 0 30px; box-sizing: border-box;
    border: 0; border-radius: 8px; background: transparent; color: ${T.dark}; cursor: pointer; padding: 0; font: inherit; transition: background-color .12s ease; }
  .rdh-tool:hover:not(:disabled) { background: ${T.coverChipBg ?? T.lightGray}; }
  .rdh-tool:disabled { color: ${T.light}; cursor: default; }
  .rdh-tool:focus-visible { outline: 2px solid ${T.blue}; outline-offset: 1px; }
  .rdh-tool[data-tip]::after { content: attr(data-tip); position: absolute; top: calc(100% + 6px); right: 0; z-index: 6; white-space: nowrap;
    padding: 4px 8px; border-radius: 6px; background: ${T.dark}; color: ${T.white}; font-size: ${T.fsXxs}px; font-weight: ${T.fwSemibold};
    letter-spacing: 0; text-transform: none; opacity: 0; pointer-events: none; transform: translateY(-2px); transition: opacity .12s ease, transform .12s ease; }
  .rdh-tool[data-tip]:hover::after, .rdh-tool[data-tip]:focus-visible::after { opacity: 1; transform: none; }
   .rdh-close { width: 32px; height: 32px; flex-basis: 32px; background: ${T.recordCloseBg}; color: ${T.white}; border-radius: 50%; }
   .rdh-close:hover:not(:disabled) { background: ${T.recordCloseBg}; opacity: .85; }
  @media (prefers-reduced-motion: reduce) { .rdh-tool, .rdh-tool[data-tip]::after { transition: none; } }
`;

/** Icon-only Share: copies the canonical absolute URL; success uses the shared
 *  toast, failure keeps inline manual-copy recovery and never reports success. */
export function ShareTool({ path, testId = "record-share" }) {
  const [failed, setFailed] = useState(false);
  const url = absoluteRecordUrl(path);
  useEffect(() => { setFailed(false); }, [path]);
  const onClick = async () => {
    const ok = await copyText(url);
    if (ok) { setFailed(false); showToast(LINK_COPIED_TOAST, { type: "success" }); }
    else setFailed(true);
  };
  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      <button type="button" className="rdh-tool" data-testid={testId} data-copy-url={url} data-tip="Share · copy link"
        aria-label={`Share: copy link to ${url}`} onClick={onClick}>
        <Share size={15} aria-hidden="true" />
      </button>
      {failed && (
        <span role="alert" data-testid={`${testId}-failed`} style={{
          position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 7, width: 280, boxSizing: "border-box",
          padding: "8px 10px", borderRadius: 8, background: T.card, border: `1px solid ${T.border}`, boxShadow: T.cardShadow,
          fontSize: T.fsXs, color: T.dark, fontWeight: T.fwSemibold, whiteSpace: "normal", textTransform: "none", letterSpacing: 0,
        }}>
          Couldn't copy to the clipboard. Copy the link manually:
          <input readOnly value={url} data-testid={`${testId}-fallback`} aria-label="Record link" autoFocus
            onFocus={(e) => e.target.select()}
            style={{ display: "block", width: "100%", boxSizing: "border-box", marginTop: 6, padding: "4px 6px", border: `1px solid ${T.border}`, borderRadius: 6, fontSize: T.fsXs, color: T.dark, background: T.tint || T.lightGray, fontFamily: "inherit" }} />
          <button type="button" data-testid={`${testId}-dismiss`} onClick={() => setFailed(false)}
            style={{ marginTop: 6, border: 0, background: "transparent", color: T.blue, fontSize: T.fsXs, fontWeight: T.fwBold, cursor: "pointer", padding: 0, fontFamily: "inherit" }}>Dismiss</button>
        </span>
      )}
    </span>
  );
}

export function RecordDrawerHeader({
  testId = "record-drawer-header", eyebrow, eyebrowColor, returnLink = null,
  titleId, titleTestId, title, titleText, titleColor = T.dark,
  meta = null, metaText, metaTestId, metaColor,
  share = null, fullPage = null, pager = null,
  onClose, closeTestId, closeLabel = "Close",
  strip = null, height = null, stripHeight = 68, padding, children = null,
}) {
  const fixed = height != null;
  const titleRef = useRef(null);
  const hasTools = Boolean(share || fullPage);
  return (
    <header data-testid={testId} data-fixed-height={fixed ? height : undefined}
      style={{
        position: "relative", boxSizing: "border-box", minWidth: 0, padding,
        ...(fixed ? { height, display: "flex", flexDirection: "column" } : { marginBottom: 12 }),
      }}>
      <style>{RECORD_HEADER_CSS}</style>
      <div data-testid={`${testId}-row1`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, height: ROW1, flex: `0 0 ${ROW1}px` }}>
        <div style={{ ...one, display: "flex", alignItems: "center", gap: 8, fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".08em", textTransform: "uppercase", color: eyebrowColor ?? T.mid }}>
          <span style={{ flexShrink: 0 }}>{eyebrow}</span>
          {returnLink && <span style={{ ...one, textTransform: "none", letterSpacing: 0 }}>{returnLink}</span>}
        </div>
        <div data-testid={`${testId}-tools`} style={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
          {share?.path && <ShareTool path={share.path} testId={share.testId} />}
          {fullPage && (
            <button type="button" className="rdh-tool" data-testid={fullPage.testId} data-tip={fullPage.label ?? "Open full page"}
              aria-label={fullPage.label ?? "Open full page"} onClick={fullPage.onClick}>
              <ExternalLink size={15} aria-hidden="true" />
            </button>
          )}
          {hasTools && pager && <span aria-hidden="true" data-testid={`${testId}-divider`} style={{ width: 1, height: 18, background: T.border, margin: "0 6px" }} />}
          {pager && <PrevNextPager variant="icon" showPosition compact {...pager} />}
          {onClose && (
            <button type="button" className="rdh-tool rdh-close" data-testid={closeTestId} data-tip="Close (Esc)" title="Close (Esc)"
              aria-label={closeLabel} onClick={onClose} style={{ marginLeft: 6 }}>
              <X size={15} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
      <h2 id={titleId} ref={titleRef} tabIndex={-1} data-testid={titleTestId} title={titleText || undefined}
        style={{
          margin: fixed ? "6px 0 0" : "2px 0 4px", fontSize: T.fsXl ?? T.fsLg, lineHeight: "28px", height: fixed ? 28 : undefined,
          fontWeight: T.fwHubTitle ?? T.fwBold, color: titleColor, outline: "none",
          ...(fixed ? one : { overflowWrap: "anywhere" }),
        }}>
        {title}
      </h2>
      {meta != null && (
        <div data-testid={metaTestId} title={metaText || undefined}
          style={{
            fontSize: T.fsXs, color: metaColor ?? T.mid, lineHeight: "20px",
            ...(fixed ? { ...one, height: 20, marginTop: 2 } : { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }),
          }}>
          {meta}
        </div>
      )}
      {strip && (fixed
        ? <div data-testid={`${testId}-strip-slot`} style={{ marginTop: "auto", height: stripHeight, flex: `0 0 ${stripHeight}px`, minWidth: 0 }}>{strip}</div>
        : strip)}
      {children}
    </header>
  );
}

export default RecordDrawerHeader;
